import type { Action } from 'svelte/action';
import { trackOnwardClick } from '../trackOnwardClick/trackOnwardClick';
import type { Section, SectionConfig, TargetType } from '../types';

export type TrackOnwardClicksParams = {
	/** The event to emit for links in this subtree, e.g. `NEWS/ONWARD_CLICK`. */
	eventName: string;
	/** The site's route map. Sites bind this once in their own wrapper. */
	config: SectionConfig;
	/** The element clicked — `nav`, `article-body`, … */
	sourceElement: string;
	/** Where the clicks happen. Defaults to the current page's section. */
	fromSection?: Section;
	/** Overrides the depth-derived target type for every link in the subtree. */
	targetType?: TargetType;
	/** The artist every link in this subtree carries, when there is one. */
	artist?: string | null;
	/** Turns the listener off without unmounting. */
	enabled?: boolean;
};

/**
 * Delegated `onward_click` tracking for a subtree of links (HB-967).
 *
 * Used where the anchors are rendered several components deep — the global nav
 * builds its links through `TopBar` → `SiteLink` → `MenuItem` — or where there
 * are no components at all, as in a CMS html blob. One listener on the container
 * catches every `<a>` beneath it, across every variant alike.
 *
 * Only plain left clicks are counted. A modified click (⌘/ctrl/shift/middle) is
 * the visitor queueing a page to read later rather than following the link now,
 * so it is not the intent this event is trying to capture. Links that open in a
 * new tab by their own markup *are* counted — see the note in the handler.
 *
 * `trackOnwardClick` does the section comparison, so in-section links inside the
 * subtree cost nothing but a resolve and are dropped.
 */
export const trackOnwardClicks: Action<HTMLElement, TrackOnwardClicksParams> = (
	node,
	params: TrackOnwardClicksParams
) => {
	let current = params;

	const handleClick = (event: MouseEvent) => {
		if (current.enabled === false) return;

		// Left button only, unmodified — see the note above on queued reads.
		if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
			return;
		}

		const anchor = (event.target as HTMLElement | null)?.closest?.('a');

		if (!anchor) return;

		// `target` is deliberately not filtered on. What matters is whether the
		// visitor stayed on the site, not whether the current page unloaded — and
		// that is a question about the destination's hostname, which
		// `getSectionFromHref` already answers. Several internal cards (the News
		// feed cards among them) open in a new tab and still carry a real crossing.

		trackOnwardClick({
			eventName: current.eventName,
			config: current.config,
			fromSection: current.fromSection,
			// The literal attribute, not `anchor.href` — the property is resolved to
			// an absolute url against the current origin, which would make a bare
			// `#cards` anchor look like a real path.
			href: anchor.getAttribute('href'),
			sourceElement: current.sourceElement,
			targetType: current.targetType,
			artist: current.artist
		});
	};

	node.addEventListener('click', handleClick);

	return {
		update: (next: TrackOnwardClicksParams) => {
			current = next;
		},
		destroy: () => {
			node.removeEventListener('click', handleClick);
		}
	};
};
